import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import Alert from '../components/UI/Alert';
import { getMemberByEmail } from '../lib/supabase';
import { getPickListValues, PICK_LIST_CATEGORIES, type PickListValue } from '../lib/pickLists';
import { CheckCircle, ExternalLink, MapPin, Calendar, Mail, ArrowRight } from 'lucide-react';
import { brandConfig } from '../brand';
import { useAuth } from '../lib/AuthProvider';
import { useAlert } from '../hooks/useAlert';

// ── Fee derivation ────────────────────────────────────────────
// Fees come from the membership_types pick list (amount column), so the
// fee schedule stays in sync with admin-configured tiers. We fall back to
// the default tier's amount, then to 25, when a type can't be resolved.

const FALLBACK_FEE = 25;

function resolveFee(
  membershipType: string,
  types: PickListValue[]
): number {
  const match = types.find(t => t.value === membershipType);
  if (match?.amount != null) return match.amount;

  const defaultType = types.find(t => t.is_default);
  if (defaultType?.amount != null) return defaultType.amount;

  return FALLBACK_FEE;
}

function resolveLabel(
  membershipType: string,
  types: PickListValue[]
): string {
  return types.find(t => t.value === membershipType)?.name || 'Membership';
}

// ── Component ─────────────────────────────────────────────────

const PaymentPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [membershipType, setMembershipType] = useState('');
  const [membershipTypes, setMembershipTypes] = useState<PickListValue[]>([]);
  const { alert, setAlert } = useAlert();

  const location = useLocation();
  const navigate = useNavigate();
  const memberId = location.state?.memberId;
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const types = await getPickListValues(PICK_LIST_CATEGORIES.MEMBERSHIP_TYPES);
        if (cancelled) return;
        setMembershipTypes(types);

        if (memberId) {
          // Came directly from registration — fee falls back to the default tier.
          setIsLoading(false);
          return;
        }

        if (authLoading) return;

        if (!user) {
          navigate('/login');
          return;
        }

        const { member, error } = await getMemberByEmail(user.email ?? '');
        if (cancelled) return;
        if (error || !member) {
          navigate('/register');
          return;
        }
        setMembershipType(member.membership_type || '');
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading payment details:', err);
        setAlert({
          type: 'error',
          message: 'Failed to load membership details. Please try again.',
        });
        setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [memberId, navigate, user, authLoading, setAlert]);

  const fee = resolveFee(membershipType, membershipTypes);
  const membershipLabel = resolveLabel(membershipType, membershipTypes);
  const actBlueUrl = brandConfig?.actBlueUrls?.membership?.[0] ?? brandConfig?.actBlueUrl ?? '';

  function handleActBlueClick() {
    window.open(actBlueUrl, '_blank', 'noopener,noreferrer');
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto py-16 px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-2/3 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
            <div className="h-48 bg-gray-200 rounded mt-8" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto py-12 px-4 sm:px-6">

        {/* ── Thank-you header ──────────────────────────── */}
        <div className="text-center mb-10">
          <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-7 w-7 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Thank you for registering!
          </h1>
          <p className="mt-2 text-gray-600 text-sm leading-relaxed">
            You're now registered with {brandConfig?.name ?? 'the club'}. To complete your membership, please pay your annual dues using one of the options below.
          </p>
        </div>

        {alert && (
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} className="mb-6" />
        )}

        {/* ── Dues explanation ──────────────────────────── */}
        <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 mb-6">

          {/* Amount */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{membershipLabel}</p>
                <p className="text-xs text-gray-500 mt-0.5">Annual dues — valid for one year</p>
              </div>
              <span className="text-xl font-bold text-gray-900">${fee}.00</span>
            </div>
          </div>

          {/* ActBlue explanation */}
          <div className="px-5 py-4">
            <p className="text-sm font-medium text-gray-900 mb-1">Paying online</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              We collect dues online through <strong>ActBlue</strong>, a secure payment platform used by Democratic clubs and campaigns across the country. Clicking the button below will open ActBlue in a new tab.
            </p>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              On the ActBlue page, select the <strong>${fee} amount</strong> and complete your payment. Once done, your membership will be activated within one business day.
            </p>

            <button
              onClick={handleActBlueClick}
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
            >
              Pay ${fee}.00 on ActBlue
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>

          {/* Pay in person */}
          <div className="px-5 py-4">
            <p className="text-sm font-medium text-gray-900 mb-1">Prefer to pay in person or by mail?</p>
            <p className="text-sm text-gray-600 mb-3">
              No problem — you can pay your dues in person at our next meeting or send a check by mail.
            </p>

            <div className="space-y-2.5">
              {(brandConfig?.nextMeetingDate || brandConfig?.nextMeetingLocation) && (
                <div className="flex items-start gap-2.5">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Next meeting: </span>
                    {[brandConfig?.nextMeetingDate, brandConfig?.nextMeetingTime]
                      .filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
              {brandConfig?.nextMeetingLocation && (
                <div className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-700">{brandConfig.nextMeetingLocation}</span>
                </div>
              )}
              {brandConfig?.contactEmail && (
                <div className="flex items-start gap-2.5">
                  <Mail className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-gray-700">
                    Questions? Email us at{' '}
                    <a
                      href={`mailto:${brandConfig.contactEmail}`}
                      className="text-primary-600 hover:underline"
                    >
                      {brandConfig.contactEmail}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Managing your account ─────────────────────── */}
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-5 py-4 mb-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Managing your account</p>
          <p className="text-sm text-gray-700 leading-relaxed mb-3">
            To access your member account in the future, go to the{' '}
            <button onClick={() => navigate('/login')} className="text-primary-600 hover:underline font-medium">
              Sign In page
            </button>{' '}
            and enter your email address. We'll send you a <strong>Magic Link</strong> — just click it and you're logged straight in, no password needed.
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            From your account you can update your personal information, manage your areas of interest, and view your membership status. We'll be adding more features over time.
          </p>
        </div>

        {/* ── Already paid? ─────────────────────────────── */}
        <p className="mt-2 text-center text-sm text-gray-500">
          Already paid?{' '}
          <button
            onClick={() => navigate('/account')}
            className="text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            Go to your account <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </p>

      </div>
    </Layout>
  );
};

export default PaymentPage;
